import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import forEach from 'lodash/forEach';
import LearnosityItemPreview from 'legacy-src/components/learnosity/LearnosityItemPreview';
import { observable } from 'legacy-packages/ft-cb';
import { TEI_TYPES } from 'legacy-src/constants/assignment';

import './McqRationales.scss';

const CLASSNAMES = {
  CORRECT_ANSWERS: 'display-correct-answers',
};

const McqRationales = (props) => {
  const [learnosityQuestionItems, setLearnosityQuestionItems] = useState({});
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [responseIndex, setResponseIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(
    observable.get('isVisible') || false,
  );
  const [updated, setUpdated] = useState(null);
  const { app, learnosityQuestion, playerContainer } = props;

  const { reference, response_ids: responseIds } = learnosityQuestion;
  const question = learnosityQuestionItems[responseIds[0]];

  useEffect(() => {
    const questions = app.questions();
    if (questions) {
      setLearnosityQuestionItems(questions);
    }
  }, [learnosityQuestion]);

  const optionElements = playerContainer.querySelectorAll(
    `[data-reference="${reference}"] .lrn_mcqgroup .lrn-mcq-option`,
  );

  const clearElClasses = (el) => {
    const { classList } = el;

    classList.remove('--incorrect');
    classList.remove('--is-selected');
    classList.remove('lrn_correct');
    classList.remove('lrn_incorrect');
  };

  useEffect(() => {
    const subscribeIsVisible = (isVisible) => {
      setIsVisible(isVisible);
    };
    observable.subscribe('isVisible', subscribeIsVisible);

    const subscribeUpdated = (updateTime) => {
      setUpdated(updateTime);
    };
    observable.subscribe(`update-current-${reference}`, subscribeUpdated);

    return () => {
      observable.unsubscribe('isVisible', subscribeIsVisible);
      observable.unsubscribe(`update-current-${reference}`, subscribeUpdated);
    };
  }, []);

  useEffect(() => {
    if (question) {
      const { validation, type } = question.getQuestion();

      if (!TEI_TYPES.includes(type)) {
        const validResponse = validation && validation.valid_response;
        const options =
          question.getProcessedOptions && question.getProcessedOptions();
        const correctResponseIndexes = validResponse.value.map((value) =>
          options.findIndex((option) => option.value === value),
        );

        forEach(optionElements, (option) => {
          clearElClasses(option);
        });

        setCorrectAnswers(correctResponseIndexes);

        if (isVisible) {
          const sortedCorrectAnswers = correctResponseIndexes.sort();
          setResponseIndex(sortedCorrectAnswers[0]);
          setSelectedOptions(sortedCorrectAnswers);
        }
      }
    }
  }, [updated, isVisible, question]);

  useEffect(() => {
    let prevClickedOption;

    const onOptionClick = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (prevClickedOption) {
        clearElClasses(prevClickedOption);
      } else {
        forEach(optionElements, (option) => {
          clearElClasses(option);
        });
      }

      const showCorrectAnswers = playerContainer.querySelector(
        `.${CLASSNAMES.CORRECT_ANSWERS}`,
      );
      if (!showCorrectAnswers) return;
      const option = event.target.closest('.lrn-mcq-option');
      const currentOptionIndex = Number.parseInt(
        option.getAttribute('data-index'),
        10,
      );
      option.classList.add('mcq-option');
      const isCorrectOption = correctAnswers.includes(currentOptionIndex);
      if (isCorrectOption) {
        option.classList.add('--correct');
      } else {
        option.classList.add('--incorrect');
      }
      option.classList.add('--is-selected');

      prevClickedOption = option;
      setResponseIndex(currentOptionIndex);
    };

    forEach(optionElements, (option, index) => {
      option.dataset.index = index;
      option.addEventListener('click', onOptionClick);
    });

    return () => {
      /**
       * Reset to correct answers
       * */
      prevClickedOption = null;
      forEach(optionElements, (option) => {
        option.removeEventListener('click', onOptionClick);
        clearElClasses(option);
        option.classList.remove('--correct');
      });
    };
  }, [isVisible, correctAnswers]);

  useEffect(() => {
    if (selectedOptions.length && isVisible) {
      const optionsContainer = playerContainer.querySelector(
        `.${CLASSNAMES.CORRECT_ANSWERS} [data-reference="${reference}"] .lrn_mcqgroup`,
      );
      if (optionsContainer) {
        let isFirstEl = true;
        selectedOptions.forEach((index) => {
          const option = optionsContainer.querySelector(
            `.lrn-mcq-option:nth-child(${index + 1})`,
          );

          // We need this timeout to give enough time to the UI and apply the styles correctly
          setTimeout(() => {
            option.classList.add('mcq-option');
            option.classList.add('--correct');
            if (isFirstEl) {
              option.classList.add('--is-selected');
              option.click();
              isFirstEl = false;
            }
          });
        });
      }
    }
  }, [selectedOptions]);

  return (
    <LearnosityItemPreview
      overrideResponseIndex={responseIndex}
      isCurrent
      showOptions={false}
      isMCQ
      isStudent={false}
      reference={reference}
      learnosityQuestion={question}
      disableShowCorrectAnswers={!isVisible}
      disableResize
      isExpanded={isVisible}
    />
  );
};

McqRationales.propTypes = {
  /**
   * Question object
   * */
  question: PropTypes.object.isRequired,
  playerContainer: PropTypes.oneOfType([
    PropTypes.instanceOf(Element),
    PropTypes.instanceOf(Document),
  ]),
};

McqRationales.defaultProps = {
  playerContainer: document,
};

export default McqRationales;
